/** Live previews for the Access tool: hatch + ladder, UTV ×2000, small flights. */

import { Group, Mesh } from "three";
import { CFG, DECK_LIFT, KR, MM, nodeY, PLANK_THICKNESS } from "../../data/haki.ts";
import { bayFrame } from "../../model/geometry.ts";
import type { Deck, Point } from "../../model/types.ts";
import { G } from "../geometries.ts";
import { M } from "../materials.ts";
import { tubeBetween, V, yawOf } from "../threeHelpers.ts";
import type { UtvPlacement } from "./types.ts";

/** Matches `LADDER_LEAN` in the deck renderer. */
const LADDER_LEAN = 0.6;

export type HatchPreview = Readonly<{ deck: Deck; side: 0 | 1; end: 0 | 1 }>;

export type SmallStairPreview = Readonly<{
  a: Point;
  b: Point;
  p: number;
  dx: number;
  dz: number;
  rise: number;
}>;

function clear(host: Group): void {
  host.clear();
}

export function showHatchPreview(host: Group, preview: HatchPreview): void {
  clear(host);

  const { deck, side, end } = preview;
  const frame = bayFrame(deck.c);
  const hatchWidth = CFG.hatch.width * MM;
  const local = (z: number) => frame.sgn * z;
  const centreZ = side ? frame.W - 0.045 - hatchWidth / 2 : 0.045 + hatchWidth / 2;
  const x = end ? frame.L - 0.55 : 0.55;
  const baseHeight = nodeY(deck.p) + KR + 0.2;

  const group = new Group();
  group.position.set(frame.a.x, baseHeight, frame.a.z);
  group.rotation.y = frame.rotY;

  const strip = new Mesh(G.box, M.nodeHot);
  strip.scale.set(frame.L - 0.13, 0.03, hatchWidth);
  strip.position.set(frame.L / 2, 0, local(centreZ));
  group.add(strip);

  const lid = new Mesh(G.box, M.hatchLid);
  lid.scale.set(0.62, 0.05, hatchWidth - 0.1);
  lid.position.set(x, 0.02, local(centreZ));
  group.add(lid);

  // Matches the placed ladder: along the bay's long axis, leaning back from the
  // opening toward the middle. See `renderHatch` in render/decks.ts.
  const lean = (end ? -1 : 1) * LADDER_LEAN;
  const footX = x + lean;
  const headX = x - lean * 0.08;
  const bottomY = (deck.p >= 4 ? nodeY(deck.p - 4) + 0.436 : nodeY(0)) - baseHeight;
  const halfWidth = (CFG.ladder.width * MM) / 2;

  for (const edge of [-halfWidth, halfWidth]) {
    group.add(
      tubeBetween(
        V(footX, bottomY + 0.05, local(centreZ + edge)),
        V(headX, 0, local(centreZ + edge)),
        G.gtube,
        M.nodeHot,
      ),
    );
  }

  const rungs = Math.max(3, Math.floor(-bottomY / (CFG.ladder.rung * MM)));
  for (let i = 1; i < rungs; i++) {
    const t = i / rungs;
    const rungX = footX + (headX - footX) * t;
    const rungY = bottomY + 0.05 + (0 - (bottomY + 0.05)) * t;
    group.add(
      tubeBetween(
        V(rungX, rungY, local(centreZ - halfWidth)),
        V(rungX, rungY, local(centreZ + halfWidth)),
        G.gtube,
        M.nodeHot,
      ),
    );
  }

  host.add(group);
}

export function showSmallStairPreview(host: Group, preview: SmallStairPreview): void {
  clear(host);

  const { a, b, p, dx, dz, rise } = preview;
  const width = CFG.utv.width * MM;
  const run = rise * MM * 1.036;

  const group = new Group();
  group.position.set((a.x + b.x) / 2, 0, (a.z + b.z) / 2);
  group.rotation.y = yawOf(dx, dz) - Math.PI / 2;

  const yTop = nodeY(p) + KR + 0.14;
  const yBottom = yTop - rise * MM;
  for (const z of [-width / 2, width / 2]) {
    group.add(tubeBetween(V(0.05, yTop, z), V(0.05 + run, yBottom, z), G.gtube, M.nodeHot));
  }

  const steps = Math.max(2, Math.round(rise / 200));
  for (let i = 1; i <= steps; i++) {
    const t = (i - 0.5) / steps;
    const step = new Mesh(G.box, M.ghost);
    step.scale.set(run / steps, 0.03, width - 0.06);
    step.position.set(0.05 + run * t, yTop - rise * MM * t, 0);
    group.add(step);
  }

  host.add(group);
}

export type LadderPreview = Readonly<{ a: Point; b: Point; p: number; dx: number; dz: number }>;

export function showLadderPreview(
  host: Group,
  preview: LadderPreview,
  groundY: (x: number, z: number) => number,
): void {
  clear(host);

  const { a, b, p, dx, dz } = preview;
  const mx = (a.x + b.x) / 2;
  const mz = (a.z + b.z) / 2;
  const group = new Group();

  group.position.set(mx, 0, mz);
  group.rotation.y = yawOf(dx, dz) - Math.PI / 2;

  const top = nodeY(p) + KR + 0.106;
  const foot = groundY(mx + dx * LADDER_LEAN, mz + dz * LADDER_LEAN);
  const halfWidth = (CFG.ladder.width * MM) / 2;

  for (const edge of [-halfWidth, halfWidth]) {
    group.add(tubeBetween(V(LADDER_LEAN, foot + 0.03, edge), V(0.04, top, edge), G.gtube, M.nodeHot));
  }

  const rungs = Math.max(1, Math.floor((top - foot) / (CFG.ladder.rung * MM)));
  for (let step = 1; step <= rungs; step++) {
    const t = step / (rungs + 0.5);
    const x = LADDER_LEAN + (0.04 - LADDER_LEAN) * t;
    const y = foot + 0.03 + (top - (foot + 0.03)) * t;
    group.add(tubeBetween(V(x, y, -halfWidth), V(x, y, halfWidth), G.gtube, M.nodeHot));
  }

  host.add(group);
}

export function showUtvPreview(host: Group, placement: UtvPlacement): void {
  clear(host);

  const { o, dx, dz, len, p } = placement;
  const width = CFG.utv.width * MM;
  const landing = 0.45;
  const run = len * MM - 2 * landing;

  const group = new Group();
  group.position.set(o.x, 0, o.z);
  group.rotation.y = yawOf(dx, dz) - Math.PI / 2;

  // Landings at deck-floor height, as the renderer draws them.
  const yLo = nodeY(p) + DECK_LIFT + PLANK_THICKNESS;
  const yHi = nodeY(p + 4) + DECK_LIFT + PLANK_THICKNESS;
  for (const z of [-width / 2, width / 2]) {
    group.add(tubeBetween(V(0, yLo, z), V(landing, yLo, z), G.gtube, M.nodeHot));
    group.add(tubeBetween(V(landing, yLo, z), V(len * MM - landing, yHi, z), G.gtube, M.nodeHot));
    group.add(tubeBetween(V(len * MM - landing, yHi, z), V(len * MM, yHi, z), G.gtube, M.nodeHot));
  }

  for (let i = 1; i <= 10; i++) {
    const t = (i - 0.5) / 10;
    const step = new Mesh(G.box, M.ghost);
    step.scale.set(run / 10, 0.03, width - 0.06);
    step.position.set(landing + run * t, yLo + (yHi - yLo) * (i / 10), 0);
    group.add(step);
  }

  host.add(group);
}

export function clearAccessPreview(host: Group | null): void {
  if (host) {
    clear(host);
  }
}
